import {
  useFloating as useFloatingUi,
  shift,
  size,
  flip,
  offset,
  hide,
  inline,
  autoUpdate,
  limitShift,
  detectOverflow,
} from '@floating-ui/react';
import type {
  Placement as FloatingPlacement,
  Strategy as FloatingStrategy,
  Boundary as FloatingBoundary,
  OffsetOptions as FloatingOffsetOptions,
  Middleware,
  UseFloatingReturn,
  SizeOptions,
} from '@floating-ui/react';

const POPOVER_PADDING = 4;

export type UseFloatingStrategyOptions = {
  nodeId?: string;
  placement: FloatingPlacement;
  floatingStrategy?: FloatingStrategy;
  offsetValue?: FloatingOffsetOptions;
  inlineEnabled?: boolean;
  flipEnabled?: boolean;
  flipOptions?: {boundary?: FloatingBoundary};
  fallbackPlacements?: FloatingPlacement[];
  hideOptions?: {referenceHidden?: boolean};
  shiftEnabled?: boolean;
  shiftOptions?: {
    padding?: number;
    altBoundary?: boolean;
    crossAxis?: boolean;
    crossAxisMinHeight?: number;
  };
  sizeOptions?: {
    maxWidth?: 'fill' | boolean;
    maxHeight?: 'fill' | boolean;
    matchWidth?: boolean;
    padding?: number;
    altBoundary?: boolean;
    rootBoundary?: SizeOptions['rootBoundary'];
  };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  additionalMiddleware?: Middleware[];
};

/**
 * Shared hook for floating-ui positioning logic used by Popover and other overlay components.
 * Encapsulates the standard middleware chain with extension points for custom middleware.
 */
function shiftCrossAxisMinHeight({
  altBoundary,
  minHeight,
  padding,
}: {
  altBoundary?: boolean;
  minHeight: number;
  padding: number;
}): Middleware {
  return {
    name: 'shiftCrossAxisMinHeight',
    async fn(state) {
      const side = state.placement.split('-')[0];
      if (side !== 'left' && side !== 'right') {
        return {};
      }

      const overflow = await detectOverflow(state, {padding, altBoundary});
      const currentHeight = state.rects.floating.height;
      const targetHeight = Math.max(currentHeight, minHeight);
      const boundaryTop = state.y + overflow.top;
      const boundaryBottom = state.y + currentHeight - overflow.bottom;
      const minY = boundaryTop;
      const maxY = boundaryBottom - targetHeight;

      return {y: maxY < minY ? minY : Math.min(Math.max(state.y, minY), maxY)};
    },
  };
}

export function useFloating<T extends HTMLDivElement>({
  nodeId,
  placement,
  floatingStrategy,
  offsetValue = POPOVER_PADDING,
  inlineEnabled,
  flipEnabled = true,
  flipOptions = {},
  fallbackPlacements,
  hideOptions,
  shiftEnabled = true,
  shiftOptions = {},
  sizeOptions,
  open,
  onOpenChange,
  additionalMiddleware = [],
}: UseFloatingStrategyOptions): UseFloatingReturn<T> {
  const crossAxisMinHeightMiddleware: false | Middleware =
    !!sizeOptions &&
    !!shiftOptions.crossAxis &&
    typeof shiftOptions.crossAxisMinHeight === 'number' &&
    shiftOptions.crossAxisMinHeight > 0
      ? shiftCrossAxisMinHeight({
          minHeight: shiftOptions.crossAxisMinHeight,
          padding: shiftOptions.padding || POPOVER_PADDING,
          altBoundary: shiftOptions.altBoundary,
        })
      : false;

  const floating = useFloatingUi<T>({
    nodeId,
    placement,
    strategy: floatingStrategy,
    whileElementsMounted: autoUpdate,
    middleware: [
      inlineEnabled && inline(),
      offset(offsetValue),
      hideOptions?.referenceHidden && hide(),
      hide({strategy: 'escaped'}),
      flip(({middlewareData}) => ({
        fallbackPlacements,
        mainAxis: flipEnabled,
        crossAxis:
          fallbackPlacements && fallbackPlacements.length > 0
            ? placement?.includes('-')
            : flipEnabled,
        altBoundary: middlewareData?.hide?.escaped,
        boundary: flipOptions.boundary,
        /**
         * The `bestFit` strategy doesn't play nicely when comparing
         * overflows that are very close to each other (e.g. less than 1px difference).
         * When paired with the `altBoundary` option, this can cause the popover to
         * flip into a position that is clipped by the viewport.
         */
        fallbackStrategy: middlewareData?.hide?.escaped
          ? 'initialPlacement'
          : 'bestFit',
      })),
      // Cross-axis shift should run before size clamping so side submenus can
      // translate vertically before max-height is calculated.
      crossAxisMinHeightMiddleware,
      sizeOptions &&
        size({
          padding: sizeOptions.padding || POPOVER_PADDING,
          altBoundary: sizeOptions.altBoundary,
          rootBoundary: sizeOptions.rootBoundary,
          apply({availableHeight, availableWidth, elements, rects}) {
            // intentionally using style here to avoid generating a new class
            // for each pixel value of width|max{width|height}.
            const style = elements.floating?.style || {};
            if (sizeOptions.maxWidth) {
              Object.assign(style, {maxWidth: `${availableWidth}px`});
            }
            if (sizeOptions.maxHeight) {
              Object.assign(style, {maxHeight: `${availableHeight}px`});
            }
            if (sizeOptions.matchWidth) {
              Object.assign(style, {width: `${rects.reference.width}px`});
            }
          },
        }),
      shift({
        mainAxis: shiftEnabled,
        crossAxis: shiftOptions.crossAxis,
        padding: shiftOptions.padding || POPOVER_PADDING,
        altBoundary: shiftOptions.altBoundary,
        limiter: limitShift(),
      }),

      ...additionalMiddleware,
    ].filter(Boolean),
    open,
    onOpenChange,
  });

  return floating;
}
